import React from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';

interface PrivacyPolicyProps {
  navigate: (to: string) => void;
}

export default function PrivacyPolicy({ navigate }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
          
          <div className="prose prose-indigo max-w-none">
            <p className="text-gray-700">
              At PM Pro AI, we are committed to protecting your privacy and safeguarding the information you share with us. This Privacy Policy outlines how we collect, use, and protect your data when you use our AI-powered project management platform.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-gray-700">
              We collect the following types of information to provide and improve our services:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Account Information: Your name, email address, and login credentials.</li>
              <li>Project Data: Any text, tasks, notes, files, or other content you add while managing your projects.</li>
              <li>AI Input/Output: Text prompts you provide to AI agents and the responses generated.</li>
              <li>API Keys: If you integrate your own LLM (Large Language Model) key (e.g., OpenAI API key), it is stored securely and used only to enable AI features for your account.</li>
              <li>Usage Data: Basic analytics such as feature usage patterns, login times, and activity history.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. How We Use Your Data</h2>
            <p className="text-gray-700">
              Your data is used to:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Provide core project management features.</li>
              <li>Enable AI-powered features such as research assistants, summaries, content generation, and task analysis.</li>
              <li>Personalize your experience by saving preferences and usage behavior.</li>
              <li>Improve our platform's performance and add new features.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Data Storage & Security</h2>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Your data is stored in secure, encrypted databases.</li>
              <li>Any third-party API keys you provide (such as OpenAI keys) are encrypted and only accessible for AI requests.</li>
              <li>We do not store or reuse AI prompt inputs or outputs outside of your account context.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Data Sharing</h2>
            <p className="text-gray-700">
              We do not sell or share your data with third parties for marketing purposes. Your project content and AI interactions are only visible to you (and your team if shared within your workspace).
            </p>
            <p className="text-gray-700 mt-4">
              However, we may use third-party services (e.g., LLM providers) to process your AI requests securely. These providers are under strict confidentiality and data processing agreements.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. AI Interactions and External APIs</h2>
            <ul className="list-disc pl-6 text-gray-700">
              <li>When you use AI agents, your prompts and context may be sent to external LLM services (like OpenAI) only for generating responses.</li>
              <li>These requests are processed in real-time and are not stored by us.</li>
              <li>We do not store AI-generated responses beyond your workspace context.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Your Rights</h2>
            <p className="text-gray-700">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Access your stored data at any time.</li>
              <li>Request deletion of your account and all associated data.</li>
              <li>Revoke your OpenAI key or any other integrations at any time from the settings.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy as our app evolves. Any changes will be posted here with an updated effective date. We encourage you to review it periodically.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-700">
              You have full control over your data:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>View and export your stored information.</li>
              <li>Update or delete your account at any time.</li>
              <li>Disconnect integrated APIs like OpenAI via the settings.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Cookies and Tracking</h2>
            <p className="text-gray-700">
              We use minimal cookies solely for:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Session management (e.g., keeping you logged in).</li>
              <li>Feature toggles and app behavior tracking.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Contact Us</h2>
            <p className="text-gray-700">
              If you have questions or concerns about your privacy, contact us at:
            </p>
            <p className="text-gray-700">
              Email: <a href="mailto:hello@pmproai.com" className="text-indigo-600 hover:text-indigo-500">hello@pmproai.com</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}